import * as React from "react";

// Open/closed state of the global layers (palette, Ask Nodus, mobile nav) and the sidebar
// collapse preference, which is remembered per browser (ui-spec §2.1).
interface ShellState {
  palette: boolean;
  setPalette: (v: boolean) => void;
  ask: boolean;
  setAsk: (v: boolean) => void;
  mobileNav: boolean;
  setMobileNav: (v: boolean) => void;
  collapsed: boolean;
  toggleCollapsed: () => void;
}

const Ctx = React.createContext<ShellState | null>(null);
const KEY = "nodus.sidebar.collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function ShellStateProvider({ children }: { children: React.ReactNode }) {
  const [palette, setPalette] = React.useState(false);
  const [ask, setAsk] = React.useState(false);
  const [mobileNav, setMobileNav] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(readCollapsed);
  const toggleCollapsed = React.useCallback(() => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(KEY, c ? "0" : "1");
      } catch {
        // Not remembered when storage is disabled.
      }
      return !c;
    });
  }, []);
  const value = React.useMemo(
    () => ({ palette, setPalette, ask, setAsk, mobileNav, setMobileNav, collapsed, toggleCollapsed }),
    [palette, ask, mobileNav, collapsed, toggleCollapsed],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useShell(): ShellState {
  const v = React.useContext(Ctx);
  if (!v) throw new Error("useShell needs a ShellStateProvider");
  return v;
}

/** The org's display data the shell shows (top bar, connection line), loaded once per org. */
export interface OrgInfo {
  name: string;
  displayName: string;
  role?: string;
}

export const OrgContext = React.createContext<{ org: OrgInfo; orgs: OrgInfo[]; projects: string[] } | null>(null);

export function useOrg() {
  const v = React.useContext(OrgContext);
  if (!v) throw new Error("useOrg is only available inside /$org");
  return v;
}

/** The project the shell is showing: the URL's, or the last used one on org pages. */
export const ProjectContext = React.createContext<string>("default");

export function useShellProject(): string {
  return React.useContext(ProjectContext);
}
